import { FC, useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import evianLogo from "../../../assets/evian/evian-f1-logo.png";
import EvianBackground from "../../../assets/evian/success-bg.jpg";
import { FanZoneData, useFanZoneWebSockets } from "../../hooks/useFanZoneWebSockets";
import { COLORS, EVIAN_COLORS } from "../../utils/Colors";

export enum EvianZonesMap {
  zone1 = 'Koval',
  zone2 = 'T-Mobile',
  zone3 = 'Flamingo',
  zone4 = 'East Harmon',
  zone5 = 'West Harmon',
}

const rowEnterFromTop = keyframes`
  0% {
    transform: translateY(-100px);
    visibility: hidden;
  }
  100% {
    transform: translateY(0);
    visibility: visible;
  }
`;

const letterAppear = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const neonBlink = keyframes`
  0%, 22%, 36%, 75%, 100% {
    color: ${EVIAN_COLORS.tableText};
    text-shadow: 0 0 10px ${EVIAN_COLORS.tableText};
  }
  28%, 33%, 82%, 92% {
    color: ${COLORS.WHITE};
    text-shadow:
      0 0 2px #0ff,
      0 0 6px #0ff,
      0 0 12px #0ff,
      0 0 20px #00b3ff,
      0 0 30px #00b3ff,
      0 0 40px #00b3ff,
      0 0 55px #009eff,
      0 0 75px #0077ff;
  }
`;

const firstPlaceBreathing = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(255, 255, 255, 0.0);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.4);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(255, 255, 255, 0.0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseRed = keyframes`
  0% {
    opacity: 0.7;
    text-shadow: 0 0 0 ${EVIAN_COLORS.primary};
  }
  50% {
    opacity: 1;
    text-shadow: 0 0 10px ${EVIAN_COLORS.primary};
  }
  100% {
    opacity: 0.7;
    text-shadow: 0 0 0 ${EVIAN_COLORS.primary};
  }
`;

const Page = styled.div`
  background: linear-gradient(180deg, ${EVIAN_COLORS.leaderboardBg} 0%, ${COLORS.WHITE} 100%);
  max-width: 1080px;
  display: flex;
  justify-content: center;
  animation: ${fadeInUp} 0.6s ease-out;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  overflow-y: auto;
  /* BrightSign OS */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-smooth: always;
  transform: translateZ(0);
  will-change: transform;
  -webkit-transform: translateZ(0);
  -moz-transform: translateZ(0);
  -ms-transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
`;

const BottomImage = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  object-fit: cover;
  z-index: -1;
`;

const Container = styled.div`
margin-block: 160px;
  width: 1080px;
  max-width: 1080px;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  & > * + * {
    margin-top: 2rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 5rem;
  
  & > * + * {
    margin-top: 0.5rem;
  }
`;

const EvianLogo = styled.div`
  color: ${EVIAN_COLORS.logoText};
  font-size: 2rem;
  font-weight: 300;
  letter-spacing: 0.1em;
  display: flex;
  align-items: center;
  
  & > * + * {
    margin-left: 0.25rem;
  }

  & > img {
    height: 160px;
  }
`;

const Title = styled.div`
  margin: 0;
  margin-bottom: 3rem;
  animation: ${neonBlink} 1.5s linear infinite;
  color: ${EVIAN_COLORS.tableText};
`;

const Char = styled.h1<{ delay: number }>`
  font-size: 3.5rem;
  font-weight: 600;
  display: inline-block;
  opacity: 0;
  line-height: 1.1;
  letter-spacing: 0.02em;
  ${({ delay }) => css`
    animation: ${letterAppear} 0.4s linear forwards;
    animation-delay: ${delay}ms;
  `}
`;

const Subtitle = styled.p`
  color: ${EVIAN_COLORS.tableText};
  text-align: center;
  max-width: 720px;
  font-size: 2.3rem;
  margin-bottom: 3rem;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 1080px;
  background: linear-gradient(180deg, ${EVIAN_COLORS.leaderboardBg} 0%, ${COLORS.WHITE} 100%);
  overflow: hidden;
  
  & > * + * {
    margin-left: 10px;
  }
  
  -webkit-display: flex;
  -moz-display: flex;
  -ms-display: flex;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
`;

const HeaderCell = styled.div`
  padding: 16px 12px;
  color: ${COLORS.BLACK};
  font-weight: 700;
  font-size: 3rem;
  letter-spacing: 0.05em;
  background: transparent;
  margin-top: 2rem;
  
  &:first-child {
    text-align: center;
    height: 120px;
    flex: 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &:nth-child(2) {
    text-align: center;
    height: 120px;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &:last-child {
    text-align: center;
    height: 120px;
    flex: 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
  }
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 1080px;
`;

const TableRow = styled.div<{ isFirst?: boolean, delayMs?: number }>`
  display: flex;
  flex-direction: row;
  width: 1080px;
  background: linear-gradient(180deg, ${EVIAN_COLORS.leaderboardBg} 0%, ${COLORS.WHITE} 100%);
  overflow: hidden;
  position: relative;
  border-radius: 10px;
  transition: transform 0.3s ease;

  & > * + * {
    margin-left: 10px;
  }

  /* Compatibilidad cross-browser */
  -webkit-display: flex;
  -moz-display: flex;
  -ms-display: flex;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;

  ${({ isFirst, delayMs = 0 }) =>
    isFirst ?
      css`
      font-weight: 700;
      animation: ${firstPlaceBreathing} 1s linear infinite;

      & > div:first-child, & > div:nth-child(2),& > div:last-child {
        animation: ${pulseRed} 1s linear infinite;
        color: ${EVIAN_COLORS.primary};
      }
    `
      : css`
      & > div:first-child, & > div:nth-child(2),& > div:last-child {
        visibility: hidden;
        animation: ${rowEnterFromTop} 1s ease-out forwards;
        animation-delay: ${delayMs}ms;
      }
    `
  }}
`;

const TableCell = styled.div<{ $isEven: boolean; $highlight?: boolean; $rank?: number }>`
  background: transparent;
  padding: 16px 12px;
  color: ${({ $isEven }) => $isEven ? EVIAN_COLORS.tableText : 'white'};
  font-weight: 500;
  font-size: 3rem;
  margin-block: 10px;
  
  &:first-child {
    text-align: center;
    font-weight: 800;
    flex: 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    color: ${EVIAN_COLORS.tableText};
  }
  
  &:nth-child(2) {
    text-align: center;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    color: ${EVIAN_COLORS.tableText};
    font-weight: 800;
  }
  
  &:last-child {
    text-align: center;
    flex: 0.5;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    color: ${EVIAN_COLORS.tableText};
  }
`;


const initialFanZones = [
  {
    id: 1,
    name: 'zone1',
    deposits: 0,
    rank: 0,
  },
  {
    id: 2,
    name: 'zone2',
    deposits: 0,
    rank: 0,
  },
  {
    id: 3,
    name: 'zone3',
    deposits: 0,
    rank: 0,
  },
  {
    id: 4,
    name: 'zone4',
    deposits: 0,
    rank: 0,
  },
  {
    id: 5,
    name: 'zone5',
    deposits: 0,
    rank: 0,
  },
];


export const EvianLeaderBoard: FC = () => {
  const [fanZonesData, setFanZonesData] = useState<FanZoneData[]>([]);

  const {
    fanZones,
    connectAll,
    disconnectAll,
  } = useFanZoneWebSockets(fanZonesData);

  const fetchFanZones = () => {
    const baseUrl = process.env.REACT_APP_BASE_API_URL;
    const url = `${baseUrl}/evian-f1/zones-map`;

    const xhr = new XMLHttpRequest();

    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4) {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (xhr.responseText.length === 0) {
            return;
          }

          let jsonResponse;
          try {
            jsonResponse = JSON.parse(xhr.responseText);
          } catch (jsonError: any) {
            console.error('JSON Parse Error:', jsonError.message);
            return;
          }

          const responseFanZones = Object.entries(jsonResponse)
            .filter(([_, id]) => id && id !== null && id !== undefined)
            .map(([name, id]) => ({
              id: id as number,
              name,
              deposits: 0,
              rank: 0,
              collectorId: (id as number).toString(),
            }))
            .filter(Boolean);

          setFanZonesData(initialFanZones.map(fz => ({
            ...fz,
            collectorId: responseFanZones.find(rfz => rfz.name === fz.name)?.collectorId.toString() || '0',
          })));
        }
      }
    };

    xhr.onerror = function () {
      console.error('XHR Network Error');
    };

    xhr.ontimeout = function () {
      console.error('XHR Timeout Error');
    };

    try {
      xhr.open('GET', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.timeout = 10000;
      xhr.send();
    } catch (error: any) {
      console.error('[EvianLeaderBoard] Error with XMLHttpRequest:', error);
    }
  };


  useEffect(() => {
    fetchFanZones();
    return () => {
      try { disconnectAll(); } catch {
        console.error('[LeaderBoard] Error disconnecting all fan zones');
      }
    };
  }, []);

  useEffect(() => {
    if (fanZonesData.length > 0) {
      connectAll();
    }
  }, [fanZonesData]);

  return (
    <Page>
      <Container>
        <LogoContainer>
          <EvianLogo>
            <img src={evianLogo} alt="evian" />
          </EvianLogo>
        </LogoContainer>

        <Title>
          {'Race to Recycle with evian'.split('').map((letter, index) => (
            <Char key={index} delay={index * 100}>
              {letter === " " ? "\u00A0" : letter}
            </Char>
          ))}
        </Title>
        <Subtitle>
          Each evian bottle deposited helps<br /> your Fan Zone climb the leaderboard.
        </Subtitle>

        <div>
          <HeaderRow>
            <HeaderCell />
            <HeaderCell>FAN ZONE</HeaderCell>
            <HeaderCell>% of<br />deposits</HeaderCell>
          </HeaderRow>
          <TableBody>
            {(() => {
              const sorted = [...fanZones].sort((a, b) => {
                if (a.deposits > 0 && b.deposits > 0) {
                  return b.deposits - a.deposits;
                }
                if (a.deposits > 0 && b.deposits === 0) return -1;
                if (a.deposits === 0 && b.deposits > 0) return 1;
                if (a.deposits === 0 && b.deposits === 0) {
                  return a.name.localeCompare(b.name);
                }
                return 0;
              });
              const totalDeposits = sorted.reduce((acc, fz) => acc + (fz.deposits || 0), 0);
              return sorted.map((fanZone, index) => {
                const rank = index + 1;
                const percent = totalDeposits > 0 ? Math.round((fanZone.deposits * 100) / totalDeposits) : 0;
                const isEven = index % 2 === 0;
                return (
                  <TableRow key={fanZone.id} isFirst={rank === 1} delayMs={(index - 1) * 1000}>
                    <TableCell $isEven={isEven} $rank={rank}>{rank}</TableCell>
                    <TableCell $isEven={isEven}>{EvianZonesMap[fanZone.name as keyof typeof EvianZonesMap]?.toUpperCase() || fanZone.name.toUpperCase()}</TableCell>
                    <TableCell $isEven={isEven}>{percent}%</TableCell>
                  </TableRow>
                );
              });
            })()}
          </TableBody>
        </div>

        <Subtitle style={{ maxWidth: 1080, marginTop: '5rem' }}>
          Recycling entrants from the winning <br />
          Fan Zone will receive an additional entry into <br />
          the Grand Prize Sweepstakes.
        </Subtitle>

      </Container>
      <BottomImage src={EvianBackground} alt="evian" />
    </Page>
  );
};